<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAccount } from 'dashboard/composables/useAccount';
import { useI18n } from 'vue-i18n';
import Banner from 'dashboard/components/ui/Banner.vue';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';

// Banner de uso de CAM (enforcement soft, Camada 3a). Avisa admins em 80%/100%.
// Lê account.limits.cam (exposto pelo endpoint /limits). Dismiss de 24h.
const WARN_THRESHOLD = 80;
const FULL_THRESHOLD = 100;
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000;

const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();
const { accountId } = useAccount();

const isDismissed = ref(false);

const getAccount = useMapGetter('accounts/getAccount');

const camUsage = computed(() => {
  const account = getAccount.value(accountId.value);
  return account?.limits?.cam || null;
});

const usagePercent = computed(() => {
  const cam = camUsage.value;
  if (!cam || !cam.total_count) return 0;
  return Math.floor((cam.consumed * 100) / cam.total_count);
});

const shouldShowBanner = computed(() => {
  if (!isAdmin.value) return false;
  if (isDismissed.value) return false;
  return usagePercent.value >= WARN_THRESHOLD;
});

const colorScheme = computed(() =>
  usagePercent.value >= FULL_THRESHOLD ? 'alert' : 'warning'
);

const bannerMessage = computed(() => {
  if (usagePercent.value >= FULL_THRESHOLD) {
    return t('CAM_USAGE.LIMIT_REACHED', { percent: usagePercent.value });
  }
  return t('CAM_USAGE.APPROACHING_LIMIT', { percent: usagePercent.value });
});

function routeToBilling() {
  store.dispatch('accounts/limits');
  window.location.href = `/app/accounts/${accountId.value}/settings/billing`;
}

function checkDismissedState() {
  const dismissedAt = LocalStorage.get(
    LOCAL_STORAGE_KEYS.CAM_USAGE_BANNER_DISMISSED_AT
  );
  if (!dismissedAt) return;
  if (Date.now() - new Date(dismissedAt).getTime() < DISMISS_DURATION_MS) {
    isDismissed.value = true;
  } else {
    LocalStorage.remove(LOCAL_STORAGE_KEYS.CAM_USAGE_BANNER_DISMISSED_AT);
  }
}

function onDismiss() {
  isDismissed.value = true;
  LocalStorage.set(
    LOCAL_STORAGE_KEYS.CAM_USAGE_BANNER_DISMISSED_AT,
    new Date().toISOString()
  );
}

onMounted(() => {
  checkDismissedState();
  store.dispatch('accounts/limits');
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Banner
    v-if="shouldShowBanner"
    :color-scheme="colorScheme"
    :banner-message="bannerMessage"
    :action-button-label="t('CAM_USAGE.OPEN_BILLING')"
    has-action-button
    has-close-button
    @primary-action="routeToBilling"
    @close="onDismiss"
  />
</template>
